import { sha256 } from "./canonical.js";
const recordIdentifier = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{2,127}$/;
const endpointIdentifier = /^[a-zA-Z0-9][a-zA-Z0-9._:@/-]{2,255}$/;
const digest = /^[A-Za-z0-9_-]{43}$/;
const dnsName = /^(?=.{1,253}\.?$)(?:\*\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.?$/;
const channels = new Set(["tls", "mtls", "grpc", "http3", "mqtt", "database", "mcp", "a2a"]);
const evidenceSources = new Set(["mesh_node_active", "mesh_node_passive", "sdk_runtime"]);
const keyEstablishment = new Set(["X25519", "X25519MLKEM768"]);
function cleanDNS(value) {
    return value.toLowerCase().replace(/\.$/, "");
}
function exactKeys(value, allowed, label) {
    const extra = Object.keys(value).filter((key) => !allowed.includes(key));
    if (extra.length)
        throw new TypeError(`${label} contains unsupported fields: ${extra.sort().join(", ")}`);
}
function observationMaterial(record) {
    return record;
}
export async function dependencyEvidenceDigest(record) {
    return sha256(observationMaterial(record));
}
export async function assertDependencyObservation(value, now = new Date()) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        throw new TypeError("dependency observation must be an object");
    const record = value;
    exactKeys(record, [
        "protocol", "id", "tenant_id", "observer_node_id", "source_endpoint_id", "target_endpoint_id",
        "target_dns_name", "target_port", "channel", "evidence_source", "first_observed_at",
        "last_observed_at", "expires_at", "sample_count", "transport", "evidence_sha256",
    ], "dependency observation");
    if (record.protocol !== "sectigo-edge.dependency-observation.v1")
        throw new TypeError("unsupported dependency observation protocol");
    for (const [label, field] of [["observation", record.id], ["tenant", record.tenant_id], ["observer", record.observer_node_id]]) {
        if (typeof field !== "string" || !recordIdentifier.test(field))
            throw new TypeError(`invalid dependency ${label} identity`);
    }
    for (const [label, field] of [["source endpoint", record.source_endpoint_id], ["target endpoint", record.target_endpoint_id]]) {
        if (typeof field !== "string" || !endpointIdentifier.test(field))
            throw new TypeError(`invalid dependency ${label} identity`);
    }
    if (record.source_endpoint_id === record.target_endpoint_id)
        throw new TypeError("dependency source and target must be distinct");
    if (!channels.has(record.channel))
        throw new TypeError("unsupported dependency channel");
    if (!evidenceSources.has(record.evidence_source))
        throw new TypeError("unsupported dependency evidence source");
    if (!Number.isSafeInteger(record.target_port) || record.target_port < 1 || record.target_port > 65_535)
        throw new TypeError("invalid dependency target port");
    if (!Number.isSafeInteger(record.sample_count) || record.sample_count < 1 || record.sample_count > 1_000_000_000)
        throw new TypeError("invalid dependency sample count");
    if (record.target_dns_name !== undefined) {
        if (typeof record.target_dns_name !== "string" || cleanDNS(record.target_dns_name) !== record.target_dns_name || !dnsName.test(record.target_dns_name)) {
            throw new TypeError("dependency target DNS name must be canonical");
        }
    }
    const first = Date.parse(record.first_observed_at);
    const last = Date.parse(record.last_observed_at);
    const expires = Date.parse(record.expires_at);
    if (![first, last, expires].every(Number.isFinite) || first > last || last > now.getTime() + 60_000 || expires <= now.getTime() || expires <= last || expires - last > 7 * 86_400_000) {
        throw new TypeError("invalid dependency observation window");
    }
    if (record.evidence_source === "sdk_runtime" && expires - last > 3_600_000)
        throw new TypeError("SDK dependency evidence lifetime exceeds one hour");
    if (record.evidence_source === "mesh_node_active" && !record.transport)
        throw new TypeError("active dependency evidence requires verified transport measurements");
    if (record.transport) {
        exactKeys(record.transport, ["tls_version", "key_establishment", "cipher_suite", "alpn", "peer_certificate_sha256", "peer_spki_sha256", "verified_peer"], "dependency transport");
        if (!new Set(["TLS1.2", "TLS1.3"]).has(record.transport.tls_version) ||
            (record.transport.key_establishment !== undefined && !keyEstablishment.has(record.transport.key_establishment)) ||
            typeof record.transport.cipher_suite !== "string" || record.transport.cipher_suite.length < 3 || record.transport.cipher_suite.length > 128 ||
            typeof record.transport.alpn !== "string" || record.transport.alpn.length < 1 || record.transport.alpn.length > 32 ||
            !digest.test(record.transport.peer_certificate_sha256) || !digest.test(record.transport.peer_spki_sha256) ||
            record.transport.verified_peer !== true) {
            throw new TypeError("invalid dependency transport evidence");
        }
    }
    if (!digest.test(record.evidence_sha256))
        throw new TypeError("invalid dependency evidence digest");
    const { evidence_sha256: claimed, ...material } = record;
    if (await dependencyEvidenceDigest(material) !== claimed)
        throw new TypeError("dependency evidence digest mismatch");
    return record;
}
export function latestDependencyEdges(observations) {
    const latest = new Map();
    for (const observation of observations) {
        const key = [observation.source_endpoint_id, observation.target_endpoint_id, observation.channel, observation.target_port].join("\u0000");
        const current = latest.get(key);
        if (!current || observation.last_observed_at > current.last_observed_at ||
            (observation.last_observed_at === current.last_observed_at && observation.id > current.id))
            latest.set(key, observation);
    }
    return [...latest.values()].sort((left, right) => left.source_endpoint_id.localeCompare(right.source_endpoint_id) ||
        left.target_endpoint_id.localeCompare(right.target_endpoint_id) ||
        left.channel.localeCompare(right.channel) || left.target_port - right.target_port);
}
export async function analyzeDependencyBlastRadius(input) {
    if (!recordIdentifier.test(input.tenantId))
        throw new TypeError("invalid dependency analysis tenant");
    const targets = [...new Set(input.targetEndpointIds)].sort();
    if (!targets.length || targets.length > 100 || targets.some((target) => !endpointIdentifier.test(target)))
        throw new TypeError("dependency analysis requires 1 to 100 valid targets");
    const maximumEdges = input.maximumEdges ?? 25_000;
    const maximumDetails = input.maximumDetails ?? 1_000;
    if (!Number.isSafeInteger(maximumEdges) || maximumEdges < 1 || maximumEdges > 100_000 || !Number.isSafeInteger(maximumDetails) || maximumDetails < 0 || maximumDetails > 5_000) {
        throw new TypeError("invalid dependency analysis bounds");
    }
    if (input.observations.length > maximumEdges * 4)
        throw new RangeError("dependency evidence history exceeds bounded analysis capacity");
    for (const observation of input.observations) {
        if (observation.tenant_id !== input.tenantId)
            throw new TypeError("cross-tenant dependency evidence is forbidden");
    }
    const graph = latestDependencyEdges(input.observations);
    if (graph.length > maximumEdges)
        throw new RangeError(`dependency graph exceeds ${maximumEdges} edges`);
    const nowMs = input.now.getTime();
    const live = graph.filter((edge) => Date.parse(edge.expires_at) > nowMs);
    const reverse = new Map();
    for (const edge of live)
        reverse.set(edge.target_endpoint_id, [...(reverse.get(edge.target_endpoint_id) ?? []), edge]);
    const queue = targets.map((endpoint) => ({ endpoint, depth: 0 }));
    const impacted = new Map();
    const visited = new Set(targets);
    while (queue.length) {
        const current = queue.shift();
        for (const edge of reverse.get(current.endpoint) ?? []) {
            if (targets.includes(edge.source_endpoint_id))
                continue;
            const depth = current.depth + 1;
            const detail = impacted.get(edge.source_endpoint_id) ?? { depth, channels: new Set(), ids: new Set(), digests: new Set() };
            detail.depth = Math.min(detail.depth, depth);
            detail.channels.add(edge.channel);
            detail.ids.add(edge.id);
            detail.digests.add(edge.evidence_sha256);
            impacted.set(edge.source_endpoint_id, detail);
            if (!visited.has(edge.source_endpoint_id)) {
                visited.add(edge.source_endpoint_id);
                queue.push({ endpoint: edge.source_endpoint_id, depth });
            }
        }
    }
    const allImpacted = [...impacted.entries()].map(([endpoint_id, detail]) => ({
        endpoint_id, depth: detail.depth, channels: [...detail.channels].sort(), evidence_ids: [...detail.ids].sort(), evidence_sha256: [...detail.digests].sort(),
    })).sort((left, right) => left.depth - right.depth || left.endpoint_id.localeCompare(right.endpoint_id));
    const impactedEndpoints = allImpacted.slice(0, maximumDetails);
    const validUntil = live.filter((edge) => impacted.has(edge.source_endpoint_id) || targets.includes(edge.target_endpoint_id)).map((edge) => edge.expires_at).sort()[0];
    const material = {
        protocol: "sectigo-edge.dependency-blast-radius.v1",
        tenant_id: input.tenantId,
        target_endpoint_ids: targets,
        ...(validUntil ? { valid_until: validUntil } : {}),
        graph_commitment_sha256: await sha256(graph),
        details_truncated: impactedEndpoints.length < allImpacted.length,
        counts: {
            graph_edges: graph.length,
            live_edges: live.length,
            stale_edges: graph.length - live.length,
            direct_dependents: allImpacted.filter((endpoint) => endpoint.depth === 1).length,
            transitive_dependents: allImpacted.filter((endpoint) => endpoint.depth > 1).length,
        },
        impacted_endpoints: impactedEndpoints,
    };
    return { ...material, analyzed_at: input.now.toISOString(), analysis_sha256: await sha256(material) };
}
//# sourceMappingURL=dependency.js.map